import { apiFetch, localStore } from "./client";
import type { DecisionLogEntry } from "../types";

/**
 * Strip markdown bold from text for SIM-T (which doesn't render markdown).
 */
function stripMarkdownForSimt(text: string): string {
  return text.replace(/\*\*(.*?)\*\*/g, "$1").replace(/__(.*?)__/g, "$1");
}

/**
 * Post a comment to a ticket via our Lambda proxy.
 */
export async function postComment(
  ticketId: string,
  message: string,
  threadName: "CORRESPONDENCE" | "WORKLOG" = "CORRESPONDENCE",
  source: "SIM_T" | "TASKEI" = "SIM_T"
): Promise<void> {
  const formattedMessage = source === "SIM_T" ? stripMarkdownForSimt(message) : message;

  await apiFetch("/actions/comment", {
    method: "POST",
    body: JSON.stringify({ ticketId, message: formattedMessage, threadName }),
  });

  addDecisionLogEntry({
    ticketId,
    timestamp: new Date().toISOString(),
    actionType: "COMMENT_POSTED",
    actor: "folaok",
    details: formattedMessage.substring(0, 200),
  });
}

/**
 * Close a ticket via our Lambda proxy.
 */
export async function closeTicket(
  ticketId: string,
  resolution: string
): Promise<void> {
  await apiFetch("/actions/close", {
    method: "POST",
    body: JSON.stringify({ ticketId, resolution }),
  });

  addDecisionLogEntry({
    ticketId,
    timestamp: new Date().toISOString(),
    actionType: "TICKET_CLOSED",
    actor: "folaok",
    details: resolution,
  });
}

/**
 * Add a decision log entry (local storage for prototype).
 */
export function addDecisionLogEntry(entry: DecisionLogEntry): void {
  const log = localStore.get<DecisionLogEntry[]>("decisionLog", []);
  log.unshift(entry);
  // Keep last 500 entries
  if (log.length > 500) log.length = 500;
  localStore.set("decisionLog", log);
}

/**
 * Get the decision log, optionally filtered.
 */
export function getDecisionLog(filters?: {
  ticketId?: string;
  actor?: string;
  keyword?: string;
}): DecisionLogEntry[] {
  let log = localStore.get<DecisionLogEntry[]>("decisionLog", []);

  if (filters?.ticketId) {
    log = log.filter((e) => e.ticketId === filters.ticketId);
  }
  if (filters?.actor) {
    log = log.filter((e) => e.actor === filters.actor);
  }
  if (filters?.keyword) {
    const kw = filters.keyword.toLowerCase();
    log = log.filter((e) => e.details.toLowerCase().includes(kw));
  }

  return log;
}
